/**
 * `kanban` namespace — user-facing strings for the top-level Kanban area
 * (`src/kanban/`). FLAT camelCase keys, one per line (ADR 0065). Plural keys use
 * i18next `_one`/`_other` suffixes (Intl.PluralRules) with `{{count}}`.
 */
export const messages = {
  // AssignedColumn — the "Assigned to me" rail
  assignedToMe: 'Assigned to me',
  assignedHint: 'A cross-board, read-only view of cards assigned to you. Open a card on its origin board to work it.',
  unclaimedWithRole: 'Unclaimed · {{role}}',
  priorityHigh: 'HIGH',
  createdByYou: 'You',
  claiming: 'Claiming…',
  claim: 'Claim',
  openOnBoard: 'Open on {{board}}',
  boardArrow: '{{board}} →',

  // AssigneeControl — assign a card to a workspace member
  unassigned: 'Unassigned',
  assignCardTitle: 'Assign this card to someone',
  assignTo: 'Assign to',
  closeAssigneePicker: 'Close assignee picker',
  membersLoadFailed: "Couldn't load members — close and reopen to retry",

  // CreateBoardModal — "Create a board"
  createBoardLabel: 'Create a board',
  newBoardEyebrow: 'New board',
  createBoardTitle: 'Create a board',
  createBoardLedeBefore: 'A board tracks work through To do → Done. Optionally connect a workflow that fires when cards hit the',
  createBoardLedeAfter: 'trigger column.',
  boardNameLabel: 'Board name',
  boardNamePlaceholder: 'e.g. Q3 onboarding',
  triggerWorkflowLabel: 'Trigger workflow',
  optionalSuffix: '· optional',
  noWorkflowOption: 'No workflow — manual board',
  workflowOptionsLoading: 'Loading workflows…',
  workflowOptionsUnavailable: 'Workflows could not be loaded. You can still create a board without a trigger.',
  owningAgentLabel: 'Owning agent',
  noOwnerOption: 'No owner — shared board',
  createBoardButton: '+ Create board',

  // KanbanBoardView — shared board renderer
  priorityLow: 'LOW',
  dueDate: 'due {{date}}',
  whyAssigned: 'Why assigned: {{reason}}',
  blocked: 'Blocked: {{note}}',
  heldChip: "Held",
  heldExplain: "Its last run was stopped or failed, so the agent won't pick it up again on its own.",
  heldRelease: "Release to agent",
  heldReleaseAria: "Release “{{title}}” so the agent can pick it up again",
  cardChangedReload: "This card changed since you loaded it. It has been reloaded — check it and try again.",
  dragCardToLane: 'Drag {{title}} to another lane',
  deleteCard: 'Delete {{title}}',
  editCard: 'Edit {{title}}',
  editTitleAria: 'Card title',
  cardUpdatedNotice: 'Updated “{{title}}”.',
  cardAddedAnnounce: 'Added “{{title}}”.',
  viewRunTitle: 'View the triggered run',
  viewRun: 'View run',
  runStatusRunning: 'Agent working',
  runStatusCompleted: 'Finished — needs review',
  runStatusFailed: 'Run failed',
  runStatusCancelled: 'Run cancelled',
  workItemAria: 'Work item: {{state}}',
  workStateProposed: 'Planned',
  workStateReady: 'Ready',
  workStateRunning: 'In progress',
  workStateBlocked: 'Needs attention',
  workStateCompleted: 'Complete',
  workStateCancelled: 'Cancelled',
  workModeAuto: 'Automatic',
  workModeManual: 'Manual',
  workScope: 'Scope: {{scope}}',
  workSource: 'From: {{source}}',
  workDependencies_one: '{{count}} dependency',
  workDependencies_other: '{{count}} dependencies',
  workNoWorkflow: 'No workflow is selected yet.',
  workRunInProgress: 'Workflow run in progress.',
  workRunSucceeded: 'Workflow run completed.',
  workRunNeedsAttention: 'The last run needs attention.',
  workStarting: 'Starting…',
  runWork: 'Run work',
  // Drag-and-drop screen-reader announcements (UX audit finding #4)
  dndInstructions: 'To pick up a card, press space or enter. While dragging, use the arrow keys to move between columns. Press space or enter again to drop the card, or press escape to cancel.',
  dndPickedUp: 'Picked up card {{title}}.',
  dndOver: 'Card {{title}} is over the {{column}} column.',
  dndDropped: 'Card {{title}} was dropped into the {{column}} column.',
  dndDroppedOutside: 'Card {{title}} was dropped outside a column — the move was cancelled.',
  dndCancelled: 'Dragging card {{title}} was cancelled.',
  // Lane-contextual actions
  startWork: 'Start work',
  markDone: 'Mark done',
  resolve: 'Resolve',
  reopen: 'Reopen',
  // Add-card form
  taskTitlePlaceholder: 'Task title…',
  taskDescriptionPlaceholder: 'Description (optional) — Markdown supported',
  taskDescriptionAria: 'Task description',
  taskSourceAria: 'Task source',
  workflowAria: 'Workflow',
  noWorkflowOptionShort: 'No workflow',
  priorityAria: 'Priority',
  priorityLowOption: 'Low',
  priorityNormalOption: 'Normal',
  priorityHighOption: 'High',
  dueDateAria: 'Due date',
  whyAssignedPlaceholder: 'Why assigned (optional)',
  // ADR 0829 P4 — handoff note on (re)assignment
  handoffDialogLabel: 'Hand off to {{name}}',
  handoffDialogTitle: 'Hand off to {{name}}',
  handoffDialogLede: 'Leave a short note so they can pick this up without asking. Nothing is sent until you assign.',
  handoffIntentLabel: 'What this is for',
  handoffDecisionsLabel: 'Decided so far',
  handoffQuestionsLabel: 'Still open',
  handoffTriedLabel: 'Already tried',
  handoffOnePerLine: '(one per line)',
  handoffConfidenceLabel: 'How sure are you?',
  handoffOptional: '(optional)',
  handoffConfidenceUnset: 'Not said',
  handoffConfidenceLow: 'Low',
  handoffConfidenceMedium: 'Medium',
  handoffConfidenceHigh: 'High',
  handoffConfidenceBasisLabel: 'Why',
  handoffRequired: '(required)',
  handoffAssignWith: 'Assign with note',
  handoffAssignWithout: 'Assign without note',
  handoffNoteShowCard: 'Handoff note',
  handoffNoteHide: 'Hide handoff note',
  handoffNoteCardTitle: 'Handoff note',
  whyAssignedAria: 'Why assigned',
  blockerPlaceholder: 'Blocker, if any (optional)',
  blockerAria: 'Blocker note',
  addCardButton: 'Add',
  addCard: '+ Add card',
  addCardShortcutHint: 'Add a card (press n while the board has focus)',
  pasteSplitOffer: 'Pasted {{count}} lines.',
  pasteSplitCreate: 'Create {{count}} cards',
  pasteSplitCreateCapped: 'Create the first {{count}} cards',
  pasteSplitKeep: 'Keep as one line',
  cardsPastedAnnounce_one: '{{count}} card added.',
  cardsPastedAnnounce_other: '{{count}} cards added.',
  wipOverLimit: 'over the work-in-progress limit',
  wipLimitEditAria: 'Set the work-in-progress limit for {{name}}',
  wipLimitLabel: 'Limit',
  wipLimitSet: 'Set',
  wipLimitClear: 'Clear limit',
  bulkBarAria: 'Bulk actions for selected cards',
  bulkSelectedCount: '{{count}} selected',
  bulkSelectedAnnounce_one: '{{count}} card selected.',
  bulkSelectedAnnounce_other: '{{count}} cards selected.',
  bulkClearedAnnounce: 'Selection cleared.',
  bulkMoveTo: 'Move to',
  bulkMovePlaceholder: 'Choose\u2026',
  bulkPriority: 'Priority',
  bulkMovedAnnounce_one: '{{count}} card moved to {{column}}.',
  bulkMovedAnnounce_other: '{{count}} cards moved to {{column}}.',
  bulkPriorityAnnounce_one: 'Priority set on {{count}} card.',
  bulkPriorityAnnounce_other: 'Priority set on {{count}} cards.',
  bulkDeletedAnnounce_one: '{{count}} card deleted.',
  bulkDeletedAnnounce_other: '{{count}} cards deleted.',
  bulkDelete: 'Delete {{count}}',
  bulkClear: 'Clear',
  deleteCardsConfirm_one: 'Delete {{count}} card?',
  deleteCardsConfirm_other: 'Delete {{count}} cards?',
  // Card sources
  sourceHuman: 'From a human',
  sourceDiscord: 'Simulated Discord',
  sourceAgent: 'From another agent',
  sourceApi: 'From an API',

  // KanbanPage — /boards route
  boardsEyebrow: 'Boards',
  boardsTitle: 'Boards',
  boardsLedePre: 'The same task boards your agents work from. Drag a card into the ',
  boardsLedeTrigger: 'To do',
  boardsLedePost: ' column to fire its workflow.',
  boardActions: 'Board actions',
  renameBoard: 'Rename board',
  duplicate: 'Duplicate',
  deleteBoard: 'Delete board',
  newBoard: '+ New board',
  waitingOnYou: '{{count}} waiting on you',
  triggers: 'Triggers:',
  loadingBoards: 'Loading boards…',
  noBoardsYet: 'No boards yet',
  noBoardsBody: 'Create a board to start tracking work — connect a workflow and it fires when cards hit the trigger column.',
  boardsUnavailableTitle: 'Couldn\'t load your boards',
  boardsUnavailableBody: 'This is a loading problem, not an empty account — any boards you own are still there. Retry before creating a new one.',
  duplicatedNotice: 'Duplicated "{{name}}" — now viewing the copy.',
  renamedNotice: 'Renamed to "{{name}}".',
  deleteBoardConfirm: 'Delete the board "{{name}}"? This removes the board and all its cards and can\'t be undone.',
  deleteCardConfirm: 'Delete the card “{{title}}”? This can\'t be undone.',
  deleteCardConfirmNoTitle: 'Delete the card? This can\'t be undone.',
  startedRunNotice: 'Started a run from "{{title}}" — it landed in a trigger lane.',
  workRunStartedNotice: 'Started the work run for "{{title}}".',
  needsReviewHeading: 'Needs review',

  // ADR 0313 D3 — heartbeat silence chip + bare-card autonomy hints
  heartbeatEvery: 'Checks every {{minutes}} min',
  heartbeatChipTitle: 'How often this agent autonomously checks this board for work',
  heartbeatOff: 'Autonomous checks off',
  heartbeatOffTitle: 'This agent never checks this board on its own — enable a heartbeat cadence in its settings',
  bareAgentTurnHint: 'Runs as an agent turn — proposed for your approval.',
  bareWontRunHint: 'Won\u2019t run on its own.',
} as const;
